import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import AdminSidebar from './components/AdminSidebar';
import StatsOverview from './components/StatsOverview';
import RecentBookings from './components/RecentBookings';
import UpcomingSlots from './components/UpcomingSlots';
import BookingsTable from './components/BookingsTable';
import AvailabilityManager from './components/AvailabilityManager';
import PaymentSettings from './components/PaymentSettings';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    flowType: 'pkce'
  }
});

interface Booking {
  id: string;
  client_name: string;
  client_email: string;
  client_phone: string;
  booking_date: string;
  booking_time: string;
  status: string;
  payment_status: string;
  payment_id: string;
  message: string;
  created_at: string;
  currency: string;
  amount_paid: number;
}

interface Slot {
  id: string;
  date: string;
  time_slot: string;
  is_available: boolean;
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') || 'overview';

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Clear existing data first to ensure fresh fetch
      setBookings([]);
      setSlots([]);
      
      const [bookingsRes, slotsRes] = await Promise.all([
        supabase
          .from('bookings')
          .select('*')
          .order('created_at', { ascending: false }),
        supabase
          .from('availability')
          .select('*')
          .order('date', { ascending: true }),
      ]);

      if (bookingsRes.error) {
        console.error('Error fetching bookings:', bookingsRes.error);
        showToast('Failed to load bookings', 'error');
      } else if (bookingsRes.data) {
        setBookings(bookingsRes.data);
      }

      if (slotsRes.error) {
        console.error('Error fetching slots:', slotsRes.error);
        showToast('Failed to load availability', 'error');
      } else if (slotsRes.data) {
        setSlots(slotsRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showToast('Failed to load data', 'error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTabChange = (tab: string) => {
    navigate(`/admin?tab=${tab}`);
  };

  const handleUpdateBookingStatus = async (id: string, status: string) => {
    // Find the booking to get its date and time
    const booking = bookings.find(b => b.id === id);
    
    const { error } = await supabase
      .from('bookings')
      .update({ status })
      .eq('id', id);

    if (error) {
      showToast('Failed to update booking status', 'error');
    } else {
      // If confirming a booking, mark the availability slot as unavailable
      if (status === 'confirmed' && booking) {
        await supabase
          .from('availability')
          .update({ is_available: false })
          .eq('date', booking.booking_date)
          .eq('time_slot', booking.booking_time);
        
        // Update local slots state
        setSlots((prev) =>
          prev.map((s) =>
            s.date === booking.booking_date && s.time_slot === booking.booking_time
              ? { ...s, is_available: false }
              : s
          )
        );
      }
      
      // If cancelling a booking, mark the slot as available again
      if (status === 'cancelled' && booking) {
        await supabase
          .from('availability')
          .update({ is_available: true })
          .eq('date', booking.booking_date)
          .eq('time_slot', booking.booking_time);
        
        // Update local slots state
        setSlots((prev) =>
          prev.map((s) =>
            s.date === booking.booking_date && s.time_slot === booking.booking_time
              ? { ...s, is_available: true }
              : s
          )
        );
      }
      
      setBookings((prev) =>
        prev.map((b) => (b.id === id ? { ...b, status } : b))
      );
      showToast(`Booking ${status} successfully`, 'success');
    }
  };

  const handleAddSlot = async (date: string, time: string) => {
    const { data, error } = await supabase
      .from('availability')
      .insert({ date, time_slot: time, is_available: true })
      .select();

    if (error) {
      console.error('Error adding slot:', error);
      showToast('Failed to add time slot', 'error');
    } else if (data && data.length > 0) {
      setSlots((prev) => [...prev, data[0]]);
      showToast('Time slot added', 'success');
    }
  };

  const handleRemoveSlot = async (id: string) => {
    const { error } = await supabase
      .from('availability')
      .update({ is_available: false })
      .eq('id', id);

    if (error) {
      showToast('Failed to remove time slot', 'error');
    } else {
      setSlots((prev) => prev.filter((s) => s.id !== id));
      showToast('Time slot removed', 'success');
    }
  };

  const handleDeleteBooking = async (id: string) => {
    const booking = bookings.find(b => b.id === id);

    const { error } = await supabase
      .from('bookings')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Delete error:', error);
      showToast('Failed to delete booking', 'error');
    } else {
      // If the booking was confirmed, free up the slot
      if (booking && booking.status === 'confirmed') {
        await supabase
          .from('availability')
          .update({ is_available: true })
          .eq('date', booking.booking_date)
          .eq('time_slot', booking.booking_time);

        setSlots((prev) =>
          prev.map((s) =>
            s.date === booking.booking_date && s.time_slot === booking.booking_time
              ? { ...s, is_available: true }
              : s
          )
        );
      }

      // Remove from local state immediately
      setBookings((prev) => prev.filter((b) => b.id !== id));
      showToast('Booking deleted successfully', 'success');
      
      // Fetch fresh data from database to ensure sync
      setTimeout(() => {
        fetchData();
      }, 500);
    }
  };

  const handleToggleSlot = async (id: string, available: boolean) => {
    const { error } = await supabase
      .from('availability')
      .update({ is_available: available })
      .eq('id', id);

    if (error) {
      showToast('Failed to update slot', 'error');
    } else {
      setSlots((prev) =>
        prev.map((s) => (s.id === id ? { ...s, is_available: available } : s))
      );
      showToast(available ? 'Slot enabled' : 'Slot disabled', 'success');
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-32">
          <div className="text-center space-y-4">
            <div className="w-10 h-10 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-sm text-gray-500">Loading dashboard...</p>
          </div>
        </div>
      );
    }

    if (currentTab === 'bookings') {
      return (
        <div>
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-900">Bookings</h2>
            <p className="text-sm text-gray-500 mt-1">Manage all client bookings and their statuses</p>
          </div>
          <BookingsTable bookings={bookings} onUpdateStatus={handleUpdateBookingStatus} onDelete={handleDeleteBooking} />
        </div>
      );
    }

    if (currentTab === 'availability') {
      return (
        <div>
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-900">Availability</h2>
            <p className="text-sm text-gray-500 mt-1">Set your available dates and time slots for consultations</p>
          </div>
          <AvailabilityManager
            slots={slots}
            onAddSlot={handleAddSlot}
            onRemoveSlot={handleRemoveSlot}
            onToggleSlot={handleToggleSlot}
          />
        </div>
      );
    }

    if (currentTab === 'payment') {
      return (
        <div>
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-900">Payment Settings</h2>
            <p className="text-sm text-gray-500 mt-1">Configure your Flutterwave payment gateway and consultation fees</p>
          </div>
          <PaymentSettings onToast={showToast} />
        </div>
      );
    }

    return (
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-slate-900">Dashboard Overview</h2>
          <p className="text-sm text-gray-500 mt-1">Welcome back! Here is a summary of your bookings</p>
        </div>
        <StatsOverview bookings={bookings} />
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <RecentBookings bookings={bookings} />
          <UpcomingSlots slots={slots} />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50/80 flex">
      <AdminSidebar activeTab={currentTab} onTabChange={handleTabChange} />

      <main className="flex-1 p-4 sm:p-6 lg:p-8 pt-16 lg:pt-8">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <div>
            <p className="text-xs text-gray-400 font-medium">
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </p>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line text-base"></i>
            Refresh
          </button>
        </div>

        {renderContent()}
      </main>

      {/* Toast notification */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-lg border transition-all animate-[slideUp_0.3s_ease-out] ${
            toast.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          <i
            className={`text-lg ${
              toast.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'
            }`}
          ></i>
          <p className="text-sm font-medium">{toast.message}</p>
        </div>
      )}
    </div>
  );
}
